import type { CardContext, CardDef } from '@/upgrades/cards';
import type { OfferCard } from '@/upgrades/pool';

/** Shared UPGRADE BAY contracts (split from upgrade-screen for file size). */
export interface ShopState {
  offer: OfferCard[];
  yellow: number;
  red: number;
  rerollPrice: number;
  /** Red-core price of the next +25 HP repair (docs/12 G6) */
  healPrice: number;
  /** Yellow-gem price of the next +1 armor unit (docs/12 O — top action row) */
  armorPrice: number;
  /** Player at full armor (max) → buy disabled */
  armorFull: boolean;
  arenaName: string;
  collection: ReadonlyArray<{ card: CardDef; count: number }>;
  /** Every owned weapon (sockets + bag) */
  inventory: ReadonlyArray<string>;
  /** Active weapons in socket order (1..3) */
  equipped: ReadonlyArray<string>;
  /** Open sockets: 1 base + Weapon Rack stacks (max 3) */
  equipCapacity: number;
  /** Spare-weapon bag slots (2 at start, bought on the bag padlock) */
  bagCap: number;
  /** Crate picked up with a full bag — swap or scrap (docs/09 wave 4) */
  pending: string | null;
  /** Picked up since the last break → "new" sparkle */
  fresh: ReadonlyArray<string>;
  /** Red price of the next socket padlock (null = all open) */
  socketPrice: number | null;
  /** Yellow price of the next bag padlock (null = bag at max) */
  bagPrice: number | null;
  /** Stored armor plates — the armor button spends these first (H3) */
  armorItems: number;
  /** Next-sector preview: boss + wave count (docs/12 B5) */
  next: { name: string; bodyKey: string; level: number; waves: number } | null;
  /** Live card context — drives the ship status panel + purchase previews */
  ctx: CardContext;
}

/** Where a crate picked up now would land (crate label, toast, HUD). */
export type CrateDestination = 'socket' | 'bag' | 'full' | 'scrap';

/** One hangar transaction (run/hangar.ts executes it). */
export type HangarAction =
  | { kind: 'equip'; weaponId: string; socket: number }
  | { kind: 'scrap'; weaponId: string }
  | { kind: 'buySocket' }
  | { kind: 'buyBag' };

export interface ShopCallbacks {
  buy(index: number): void;
  /** Sockets, bag, swap-or-scrap crate, padlocks */
  hangar(action: HangarAction): void;
  /** Keep-lock toggle on an offer card (padlock button / X shortcut) */
  toggleLock(index: number): void;
  reroll(): void;
  heal(): void;
  /** Buy +1 armor unit with yellow gems (docs/12 O top action row) */
  buyArmor(): void;
  /** Apply one stored armor plate (the armor button uses these first) */
  useArmor(): void;
  continueRun(): void;
  /** Header gear (mouse / touch): opens the pause screen with ⌂ MAIN MENU */
  openMenu(): void;
}

/** Compact (phone) shop pages — one tab each (polish 1, docs/06). */
export type ShopPage = 'cards' | 'hangar' | 'status';

export type FocusItem =
  | { kind: 'card'; index: number }
  /** Socket tile 0..2: weapon / empty target / padlock buy */
  | { kind: 'socket'; index: number }
  /** Bag weapon or the waiting swap-or-scrap crate */
  | { kind: 'weapon'; weaponId: string }
  /** The bag padlock (next slot) */
  | { kind: 'bagBuy' }
  | { kind: 'chip'; cardId: string }
  | { kind: 'reroll' }
  | { kind: 'heal' }
  | { kind: 'buyArmor' }
  | { kind: 'continue' }
  /** Compact shop page tab (never in the desktop focus list) */
  | { kind: 'tab'; page: ShopPage };

/** Bag contents in display order: owned weapons not in a socket. */
export function bagWeapons(s: ShopState): string[] {
  return s.inventory.filter((id) => !s.equipped.includes(id));
}

/** Socket i is focusable: open, or the next padlock (chained ones are not). */
export function socketFocusable(s: ShopState, i: number): boolean {
  return i < s.equipCapacity || (i === s.equipCapacity && s.socketPrice !== null);
}

/**
 * Focus group order (layout v4): offer cards FIRST — the screen's main
 * decision and the initial focus — then the reroll bar, the header vital
 * buys (repair / armor), collection chips, sockets, bag (+ waiting crate,
 * bag padlock), CONTINUE.
 * Movement itself is spatial (shop-nav.ts); this order only sets the
 * initial focus and keeps the flat list stable for retention.
 */
export function buildFocusGroups(state: ShopState, maxChips: number): FocusItem[][] {
  return [
    state.offer.map((_, index) => ({ kind: 'card', index }) as FocusItem),
    [{ kind: 'reroll' } as FocusItem],
    [{ kind: 'heal' } as FocusItem, { kind: 'buyArmor' } as FocusItem],
    state.collection
      .slice(0, maxChips)
      .map((e) => ({ kind: 'chip', cardId: e.card.id }) as FocusItem),
    [0, 1, 2]
      .filter((index) => socketFocusable(state, index))
      .map((index) => ({ kind: 'socket', index }) as FocusItem),
    [
      ...bagWeapons(state).map((weaponId) => ({ kind: 'weapon', weaponId }) as FocusItem),
      ...(state.bagPrice !== null ? [{ kind: 'bagBuy' } as FocusItem] : []),
      ...(state.pending ? [{ kind: 'weapon', weaponId: state.pending } as FocusItem] : []),
    ],
    [{ kind: 'continue' } as FocusItem],
  ].filter((g) => g.length > 0);
}
